/**
 * Eén inline bewerkbaar veld, gedeeld door Fan info en Model info (15/16-10-2026,
 * Jordy). Slaat op bij het verlaten van het veld (of Enter in een regel); de
 * aanroeper bepaalt waarheen (bestaande routes).
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export type Stand = '' | 'saving' | 'saved' | 'error'

export function StandTekst({ stand, fout }: { stand: Stand; fout?: string }) {
  if (!stand) return null
  const tekst = stand === 'saving' ? 'Saving…' : stand === 'saved' ? 'Saved' : (fout || 'Not saved')
  return (
    <span className="mut2" data-testid="veld-stand"
          style={{ fontSize: 11, color: stand === 'error' ? 'var(--bad)' : undefined }}>
      {tekst}
    </span>
  )
}

/** Eén bewerkbaar veld; slaat op bij het verlaten (of Enter in een regel). */
export function Veld({ label, waarde, meerregelig, opslaan, focusSleutel, testid, extra }: {
  label: string
  waarde: string
  meerregelig?: boolean
  opslaan: (waarde: string) => Promise<void>
  focusSleutel?: number
  testid?: string
  extra?: ReactNode
}) {
  const [concept, setConcept] = useState(waarde)
  const [stand, setStand] = useState<Stand>('')
  const [fout, setFout] = useState('')
  const bezig = useRef(false)
  const veld = useRef<HTMLTextAreaElement & HTMLInputElement>(null)
  // Nieuwe waarde van de server (na opslaan of verversen): overnemen zolang
  // de chatter hier niet zit te typen.
  useEffect(() => {
    if (document.activeElement !== veld.current) setConcept(waarde)
  }, [waarde])
  useEffect(() => {
    if (focusSleutel) veld.current?.focus()
  }, [focusSleutel])

  const bewaar = async () => {
    if (bezig.current || concept === waarde) return
    bezig.current = true
    setStand('saving'); setFout('')
    try {
      await opslaan(concept)
      setStand('saved')
    } catch (e) {
      setStand('error'); setFout(String((e as Error)?.message || e))
    } finally {
      bezig.current = false
    }
  }
  const leeg = !waarde.trim()
  return (
    <div className="fanveld" data-testid={testid} data-leeg={leeg ? '1' : '0'}>
      <div className="row" style={{ alignItems: 'center', gap: 6 }}>
        <span className="mut2" style={{ fontSize: 11.5 }}>{label}</span>
        <span style={{ flex: 1 }} />
        <StandTekst stand={stand} fout={fout} />
        {extra}
      </div>
      {meerregelig ? (
        <textarea ref={veld} className="inp" rows={leeg ? 1 : 3} value={concept}
                  placeholder="—" aria-label={label}
                  onChange={(e) => { setConcept(e.target.value); setStand('') }}
                  onBlur={bewaar} />
      ) : (
        <input ref={veld} className="inp" value={concept} placeholder="—" aria-label={label}
               onChange={(e) => { setConcept(e.target.value); setStand('') }}
               onBlur={bewaar}
               onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }} />
      )}
    </div>
  )
}

